"use client"

import { useMemo, useState } from "react"
import { MotionConfig, motion } from "framer-motion"
import { useCart, formatEUR } from "@/lib/cart-store"
import type { MenuItem } from "@/lib/restaurants/types"
import { FilaCactos } from "./maria-bonita-art"

type Categoria = { id: string; nome: string; icone?: string }

const OUT = [0.23, 1, 0.32, 1] as const

/** Preço 0 = a casa ainda não confirmou. Nunca inventamos um valor. */
const semPreco = (i: MenuItem) => !i.preco || i.preco <= 0

export function MariaBonitaMenu({
  items,
  categorias,
}: {
  items: MenuItem[]
  categorias: Categoria[]
  slug: string
}) {
  const [catAtiva, setCatAtiva] = useState<string>(categorias[0]?.id ?? "")
  const { addItem, lines } = useCart()

  const comSeparadores = categorias.length > 1
  const visiveis = useMemo(
    () => (comSeparadores ? items.filter((i) => i.categoria === catAtiva) : items),
    [items, catAtiva, comSeparadores]
  )
  const faltamPrecos = useMemo(() => items.some(semPreco), [items])

  const qtdDe = (id: string) => lines.find((l) => l.itemId === id)?.quantidade ?? 0

  return (
    <MotionConfig reducedMotion="user">
    <div className="max-w-3xl mx-auto px-5 pb-4">
      {/* separadores de categoria — só quando há mais do que uma */}
      {comSeparadores && (
        <nav
          aria-label="Categorias da ementa"
          className="sticky top-[64px] z-20 -mx-5 px-5 py-3 flex gap-2.5 overflow-x-auto md:flex-wrap md:justify-center no-scrollbar"
          style={{
            backgroundColor: "color-mix(in srgb, var(--ph-bg) 93%, transparent)",
            backdropFilter: "blur(12px)",
            borderBottom: "2px solid var(--ph-ink)",
          }}
        >
          {categorias.map((c) => {
            const ativa = catAtiva === c.id
            return (
              <button
                key={c.id}
                onClick={() => setCatAtiva(c.id)}
                aria-pressed={ativa}
                className="bonita-press bonita-display shrink-0 rounded-full px-5"
                style={{
                  minHeight: 46,
                  fontSize: 15,
                  border: "2px solid var(--ph-ink)",
                  backgroundColor: ativa ? "var(--ph-ink)" : "#F7EEDD",
                  color: ativa ? "var(--ph-accent)" : "var(--ph-ink)",
                  boxShadow: ativa ? "none" : "2px 2px 0 var(--ph-ink)",
                }}
              >
                {c.nome}
              </button>
            )
          })}
        </nav>
      )}

      {/* aviso honesto enquanto a casa não entrega a carta com preços */}
      {faltamPrecos && (
        <div
          className="bonita-outline-sm rounded-2xl px-5 py-4 mt-6"
          style={{ backgroundColor: "var(--ph-card)" }}
        >
          <p
            className="bonita-label"
            style={{ color: "var(--ph-accent)", fontSize: 10, letterSpacing: "0.2em" }}
          >
            Ementa em atualização
          </p>
          <p className="mt-2" style={{ color: "#F7EEDD", fontSize: 14.5, lineHeight: 1.6 }}>
            Estamos a afinar a carta completa com preços. Para encomendar já,
            liga-nos ou fala connosco pelo WhatsApp.
          </p>
        </div>
      )}

      <ul className="mt-6 space-y-4">
        {visiveis.map((item, i) => {
          const qtd = qtdDe(item.id)
          const porConfirmar = semPreco(item)

          return (
            <motion.li
              key={item.id}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.42, delay: Math.min(i * 0.05, 0.2), ease: OUT }}
              className="bonita-card bonita-outline rounded-[22px] overflow-hidden flex flex-col sm:flex-row"
              style={{ backgroundColor: "var(--ph-card)" }}
            >
              {item.imagem && (
                <div className="sm:w-44 sm:shrink-0 sm:order-2 overflow-hidden">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={item.imagem}
                    alt={item.nome}
                    className="block w-full h-40 sm:h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}

              <div className="flex-1 p-5 min-w-0 sm:order-1">
                <div className="flex items-start gap-2.5">
                  <h3
                    className="bonita-display leading-tight"
                    style={{ color: "#F7EEDD", fontSize: 21 }}
                  >
                    {item.nome}
                  </h3>
                  {item.popular && (
                    <span
                      className="bonita-label shrink-0 rounded px-2 py-1"
                      style={{
                        backgroundColor: "var(--ph-accent)",
                        color: "var(--ph-ink)",
                        fontSize: 9,
                        letterSpacing: "0.16em",
                      }}
                    >
                      Top
                    </span>
                  )}
                </div>

                {item.descricao && (
                  <p
                    className="mt-2"
                    style={{ color: "rgba(247,238,221,0.78)", fontSize: 14, lineHeight: 1.6 }}
                  >
                    {item.descricao}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between gap-3">
                  {porConfirmar ? (
                    <span
                      className="bonita-label"
                      style={{
                        color: "var(--ph-accent)",
                        fontSize: 10,
                        letterSpacing: "0.18em",
                      }}
                    >
                      Preço a confirmar
                    </span>
                  ) : (
                    <span
                      className="bonita-display"
                      style={{ color: "var(--ph-accent)", fontSize: 24, lineHeight: 1 }}
                    >
                      {formatEUR(item.preco)}
                    </span>
                  )}

                  {porConfirmar ? (
                    <a
                      href="tel:+351933067611"
                      className="bonita-press rounded-full px-4 font-semibold inline-flex items-center"
                      style={{
                        minHeight: 44,
                        fontSize: 13,
                        backgroundColor: "transparent",
                        color: "#F7EEDD",
                        border: "2px solid rgba(247,238,221,0.5)",
                      }}
                    >
                      Perguntar
                    </a>
                  ) : (
                    <button
                      onClick={() =>
                        addItem({
                          id: item.id,
                          nome: item.nome,
                          preco: item.preco,
                          imagem: item.imagem,
                        })
                      }
                      className="bonita-press bonita-display rounded-full px-5 inline-flex items-center gap-2"
                      style={{
                        minHeight: 44,
                        fontSize: 14,
                        backgroundColor: "var(--ph-accent)",
                        color: "var(--ph-ink)",
                        border: "2px solid var(--ph-ink)",
                      }}
                    >
                      {qtd > 0 && (
                        <span
                          className="rounded-full w-5 h-5 flex items-center justify-center"
                          style={{
                            backgroundColor: "var(--ph-ink)",
                            color: "var(--ph-accent)",
                            fontSize: 11,
                          }}
                        >
                          {qtd}
                        </span>
                      )}
                      Adicionar
                    </button>
                  )}
                </div>
              </div>
            </motion.li>
          )
        })}
      </ul>

      <FilaCactos className="mt-12 overflow-hidden scale-75 origin-bottom" color="var(--ph-ink)" opacity={0.72} />

      <p
        className="mt-6 mb-4 text-center"
        style={{ color: "var(--ph-ink-muted)", fontSize: 12, lineHeight: 1.7 }}
      >
        Feito na hora — pode haver espera nas horas de ponta.
        <br />
        Todos os preços incluem IVA à taxa em vigor.
      </p>
    </div>
    </MotionConfig>
  )
}
